import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum ProvisioningMessageType {
  PROVISIONING_SCRIPT_DEPLOY = 'PROVISIONING_SCRIPT_DEPLOY',
  PROVISIONING_SCRIPT_REVOKE = 'PROVISIONING_SCRIPT_REVOKE',
  PROVISIONING_SCRIPT_RESULT = 'PROVISIONING_SCRIPT_RESULT',
  PROVISIONING_SCRIPT_STARTED = 'PROVISIONING_SCRIPT_STARTED',
  PROVISIONING_MATCHMAKING_PAUSE = 'PROVISIONING_MATCHMAKING_PAUSE',
  PROVISIONING_MATCHMAKING_RESUME = 'PROVISIONING_MATCHMAKING_RESUME',
  PROVISIONING_EXECUTION_UPDATE = 'PROVISIONING_EXECUTION_UPDATE',
  PROVISIONING_AI_REVIEW_COMPLETE = 'PROVISIONING_AI_REVIEW_COMPLETE',
  PROVISIONING_QUEUE_STATUS = 'PROVISIONING_QUEUE_STATUS',
}

const provisioningScriptDeployPayloadSchema = z.object({
  deploymentId: z.string(),
  executionId: z.string(),
  scriptId: z.string(),
  scriptName: z.string(),
  scriptType: z.string(),
  completeScript: z.string(),
  signature: z.string(),
  pauseMatchmaking: z.boolean(),
  deploymentMode: z.string(),
  timeoutSeconds: z.number().optional(),
  /** 0-based index of the step being dispatched (for multi-step scripts). Omitted on legacy single-step deploys. */
  stepIndex: z.number().int().min(0).optional(),
  /** Human-readable name of this step. */
  stepName: z.string().optional(),
  /** Total step count for this deployment. */
  stepCount: z.number().int().min(1).optional(),
});

const provisioningScriptRevokePayloadSchema = z.object({
  deploymentId: z.string(),
  reason: z.string().optional(),
  forceKill: z.boolean().optional(),
  exitGuestMode: z.boolean().optional(),
  rebootIfSafe: z.boolean().optional(),
});

const provisioningScriptResultPayloadSchema = z.object({
  deploymentId: z.string(),
  executionId: z.string(),
  machineId: z.string(),
  status: z.enum(['succeeded', 'failed', 'timeout', 'cancelled']),
  exitCode: z.number().optional(),
  stdout: z.string().optional(),
  stderr: z.string().optional(),
  error: z.union([z.string(), z.null()]).optional(),
  /** Echoes the deploy message's `stepIndex` so the server can route results to the correct step. */
  stepIndex: z.number().int().min(0).optional(),
});

const provisioningScriptStartedPayloadSchema = z.object({
  deploymentId: z.string(),
  executionId: z.string(),
  machineId: z.string(),
  /** Echoes the deploy message's `stepIndex` so the server can route the started event to the correct step. */
  stepIndex: z.number().int().min(0).optional(),
});

const provisioningMatchmakingPausePayloadSchema = z.object({
  deploymentId: z.string(),
  machineId: z.string(),
});

const provisioningMatchmakingResumePayloadSchema = z.object({
  deploymentId: z.string(),
  machineId: z.string(),
});

const provisioningExecutionUpdatePayloadSchema = z.object({
  deploymentId: z.string(),
  executionId: z.string(),
  machineId: z.string(),
  status: z.string(),
  exitCode: z.number().optional(),
  error: z.union([z.string(), z.null()]).optional(),
  stdoutUrl: z.string().optional(),
});

const provisioningAIReviewCompletePayloadSchema = z.object({
  scriptId: z.string(),
  status: z.enum(['completed', 'failed']),
  result: z.any().optional(),
  error: z.string().optional(),
});

const provisioningQueueStatusPayloadSchema = z.object({
  machineId: z.string(),
  pendingCount: z.number(),
  isExecuting: z.boolean(),
  currentDeploymentId: z.string().optional(),
});

const provisioningScriptDeploySchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_SCRIPT_DEPLOY) })
  .merge(provisioningScriptDeployPayloadSchema);

const provisioningScriptRevokeSchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_SCRIPT_REVOKE) })
  .merge(provisioningScriptRevokePayloadSchema);

const provisioningScriptResultSchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_SCRIPT_RESULT) })
  .merge(provisioningScriptResultPayloadSchema);

const provisioningScriptStartedSchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_SCRIPT_STARTED) })
  .merge(provisioningScriptStartedPayloadSchema);

const provisioningMatchmakingPauseSchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_MATCHMAKING_PAUSE) })
  .merge(provisioningMatchmakingPausePayloadSchema);

const provisioningMatchmakingResumeSchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_MATCHMAKING_RESUME) })
  .merge(provisioningMatchmakingResumePayloadSchema);

const provisioningExecutionUpdateSchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_EXECUTION_UPDATE) })
  .merge(provisioningExecutionUpdatePayloadSchema);

const provisioningAIReviewCompleteSchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_AI_REVIEW_COMPLETE) })
  .merge(provisioningAIReviewCompletePayloadSchema);

const provisioningQueueStatusSchema = baseMessageSchema
  .extend({ type: z.literal(ProvisioningMessageType.PROVISIONING_QUEUE_STATUS) })
  .merge(provisioningQueueStatusPayloadSchema);

export const provisioningMessageSchema = z.discriminatedUnion('type', [
  provisioningScriptDeploySchema,
  provisioningScriptRevokeSchema,
  provisioningScriptResultSchema,
  provisioningScriptStartedSchema,
  provisioningMatchmakingPauseSchema,
  provisioningMatchmakingResumeSchema,
  provisioningExecutionUpdateSchema,
  provisioningAIReviewCompleteSchema,
  provisioningQueueStatusSchema,
]);

export type ProvisioningMessage = z.infer<typeof provisioningMessageSchema>;

export function generateProvisioningMessage(
  type: ProvisioningMessageType,
  payload: Record<string, unknown>,
): ProvisioningMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };
  switch (type) {
    case ProvisioningMessageType.PROVISIONING_SCRIPT_DEPLOY:
      return provisioningScriptDeploySchema.parse({ ...base, ...payload });
    case ProvisioningMessageType.PROVISIONING_SCRIPT_REVOKE:
      return provisioningScriptRevokeSchema.parse({ ...base, ...payload });
    case ProvisioningMessageType.PROVISIONING_SCRIPT_RESULT:
      return provisioningScriptResultSchema.parse({ ...base, ...payload });
    case ProvisioningMessageType.PROVISIONING_SCRIPT_STARTED:
      return provisioningScriptStartedSchema.parse({ ...base, ...payload });
    case ProvisioningMessageType.PROVISIONING_MATCHMAKING_PAUSE:
      return provisioningMatchmakingPauseSchema.parse({ ...base, ...payload });
    case ProvisioningMessageType.PROVISIONING_MATCHMAKING_RESUME:
      return provisioningMatchmakingResumeSchema.parse({ ...base, ...payload });
    case ProvisioningMessageType.PROVISIONING_EXECUTION_UPDATE:
      return provisioningExecutionUpdateSchema.parse({ ...base, ...payload });
    case ProvisioningMessageType.PROVISIONING_AI_REVIEW_COMPLETE:
      return provisioningAIReviewCompleteSchema.parse({ ...base, ...payload });
    case ProvisioningMessageType.PROVISIONING_QUEUE_STATUS:
      return provisioningQueueStatusSchema.parse({ ...base, ...payload });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled provisioning message type: ${exhaustiveCheck}`);
    }
  }
}
